import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  blockAccountsApi,
  type BlockAccountDto,
  type CreateBlockAccountRequest,
  type CreateSmaRequest,
  type ExportConfig,
  type LinkPaperAccountRequest,
  type PatchBlockAccountRequest,
  type PatchSmaRequest,
  type RunDailyExportResult,
  type SmaExportRow,
  type SubManagedAccountDto,
} from "@/lib/api";

const THIRTY_SECONDS = 30 * 1000;

/**
 * SFWL-012: query keys for the block-accounts surface. `invalidateAll` is
 * used by every mutation below — it fuzzy-matches both the list and the
 * export-summary families regardless of which `tenantId` filter is active,
 * since a mutation to one tenant's blocks can change any cached view of it.
 */
const blockAccountsKeys = {
  all: ["block-accounts"] as const,
  list: (tenantId?: string) => ["block-accounts", "list", tenantId ?? "all"] as const,
  exportSummary: (tenantId?: string) =>
    ["block-accounts", "export-summary", tenantId ?? "all"] as const,
  exportConfig: (tenantId: string) =>
    ["block-accounts", "export-config", tenantId] as const,
};

/**
 * SFWL-012: super-admin block account + SMA management. `tenantId` is an
 * explicit filter (this surface is platform-role gated, not tenant-scoped —
 * see the locked spec's OQ4), distinct from the active subdomain tenant used
 * elsewhere via `useTenant()`. Omit it to list across all tenants.
 *
 * The server enforces `SovrunAdminGuard` on every route including GETs; a
 * non-admin session gets a 403 `Error` from `handleResponse` on the first
 * call, which callers surface via `error` / mutation `onError` (toast is the
 * page's responsibility — no client-side role plumbing here).
 */
export function useBlockAccounts(tenantId?: string) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: blockAccountsKeys.list(tenantId),
    queryFn: () => blockAccountsApi.list(tenantId),
    staleTime: THIRTY_SECONDS,
    // This surface is SovrunAdminGuard-gated end-to-end (incl. GETs) and
    // always 403s for non-admins; handleResponse discards response.status,
    // so a status-aware retry predicate isn't available without a client
    // refactor. retry:false avoids React Query's default 3-retry storm
    // (up to 8 requests, ~7s delay) on every non-admin page load.
    retry: false,
  });

  const invalidateAll = () =>
    queryClient.invalidateQueries({ queryKey: blockAccountsKeys.all });

  const createBlockMutation = useMutation({
    mutationFn: (data: CreateBlockAccountRequest) => blockAccountsApi.createBlock(data),
    onSuccess: invalidateAll,
  });

  const patchBlockMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: PatchBlockAccountRequest }) =>
      blockAccountsApi.patchBlock(id, data),
    onSuccess: invalidateAll,
  });

  const deleteBlockMutation = useMutation({
    mutationFn: (id: string) => blockAccountsApi.deleteBlock(id),
    onSuccess: invalidateAll,
  });

  const addSmaMutation = useMutation({
    mutationFn: ({ blockId, data }: { blockId: string; data: CreateSmaRequest }) =>
      blockAccountsApi.addSma(blockId, data),
    onSuccess: invalidateAll,
  });

  const patchSmaMutation = useMutation({
    mutationFn: ({
      blockId,
      smaId,
      data,
    }: {
      blockId: string;
      smaId: string;
      data: PatchSmaRequest;
    }) => blockAccountsApi.patchSma(blockId, smaId, data),
    onSuccess: invalidateAll,
  });

  const deleteSmaMutation = useMutation({
    mutationFn: ({ blockId, smaId }: { blockId: string; smaId: string }) =>
      blockAccountsApi.deleteSma(blockId, smaId),
    onSuccess: invalidateAll,
  });

  const recomputeMutation = useMutation({
    mutationFn: (blockId: string) => blockAccountsApi.recompute(blockId),
    onSuccess: invalidateAll,
  });

  const linkAccountMutation = useMutation({
    mutationFn: ({
      blockId,
      smaId,
      data,
    }: {
      blockId: string;
      smaId: string;
      data: LinkPaperAccountRequest;
    }) => blockAccountsApi.linkAccount(blockId, smaId, data),
    onSuccess: invalidateAll,
  });

  return {
    blocks: query.data?.blocks ?? [],
    isLoading: query.isPending,
    error: query.error as Error | null,

    createBlock: createBlockMutation.mutateAsync,
    patchBlock: patchBlockMutation.mutateAsync,
    deleteBlock: deleteBlockMutation.mutateAsync,
    addSma: addSmaMutation.mutateAsync,
    patchSma: patchSmaMutation.mutateAsync,
    deleteSma: deleteSmaMutation.mutateAsync,
    recompute: recomputeMutation.mutateAsync,
    linkAccount: linkAccountMutation.mutateAsync,

    isCreatingBlock: createBlockMutation.isPending,
    isPatchingBlock: patchBlockMutation.isPending,
    isDeletingBlock: deleteBlockMutation.isPending,
    isAddingSma: addSmaMutation.isPending,
    isPatchingSma: patchSmaMutation.isPending,
    isDeletingSma: deleteSmaMutation.isPending,
    isRecomputing: recomputeMutation.isPending,
    isLinkingAccount: linkAccountMutation.isPending,
  };
}

/**
 * SFWL-012: standalone export-summary preview query (HTTP mirror of the
 * `summaryForExport` service call #70 consumes). Separate from
 * `useBlockAccounts` since the page renders it as its own preview table with
 * an independent loading state.
 */
export function useBlockAccountsExportSummary(tenantId?: string) {
  const query = useQuery<SmaExportRow[]>({
    queryKey: blockAccountsKeys.exportSummary(tenantId),
    queryFn: () => blockAccountsApi.exportSummary(tenantId),
    staleTime: THIRTY_SECONDS,
    // See useBlockAccounts — same always-403-for-non-admins rationale.
    retry: false,
  });

  return {
    rows: query.data ?? [],
    isLoading: query.isPending,
    error: query.error as Error | null,
  };
}

/**
 * SFWL-013: manual "Run export now" trigger. No cache to invalidate — a
 * send doesn't change block/SMA/export-summary data, only mails a CSV
 * snapshot of it.
 */
export function useRunDailyExport() {
  return useMutation({
    mutationFn: (tenantId?: string) => blockAccountsApi.runDailyExport(tenantId),
  });
}

/**
 * SFWL-013: a tenant's daily export recipient list + timezone. Disabled
 * until a `tenantId` is picked — mirrors `useExportSummary`'s per-tenant
 * gating on this admin-only, non-tenant-scoped page.
 */
export function useExportConfig(tenantId?: string) {
  const query = useQuery<ExportConfig>({
    queryKey: blockAccountsKeys.exportConfig(tenantId ?? ""),
    queryFn: () => blockAccountsApi.getExportConfig(tenantId as string),
    enabled: !!tenantId,
    staleTime: THIRTY_SECONDS,
    // See useBlockAccounts — same always-403-for-non-admins rationale.
    retry: false,
  });

  return {
    config: query.data,
    isLoading: query.isPending,
    error: query.error as Error | null,
  };
}

/**
 * SFWL-013: save a tenant's daily export recipient list + timezone. Writes
 * the server-returned value straight into the query cache instead of
 * invalidating — an invalidate-triggered refetch would briefly re-derive the
 * form inputs from stale cached data before the refetch resolves, flickering
 * the just-saved value back to the prior one (confirmed review finding).
 */
export function useSetExportConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenantId, data }: { tenantId: string; data: ExportConfig }) =>
      blockAccountsApi.setExportConfig(tenantId, data),
    onSuccess: (saved, { tenantId }) => {
      queryClient.setQueryData(blockAccountsKeys.exportConfig(tenantId), saved);
    },
  });
}

export type {
  BlockAccountDto,
  SubManagedAccountDto,
  SmaExportRow,
  RunDailyExportResult,
  ExportConfig,
};
